import React, { FC, useCallback, useEffect, useState } from 'react';
import { Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useFetch, useUser } from '../../../../shared/hooks';
import apiRoutes from '../../../../../config/api/routes';
import {
  CurriculumGoalType,
  IBookCurriculumGoal,
} from '../../../../../config/api/models/CurriculumGoal';
import { Message } from '../../../../shared/components';
import CurriculumGoalsByTypeCombobox from './CurriculumGoalsByTypeCombobox';
import TrajectoryCurriculumGoalsList from './TrajectoryCurriculumGoalsList';
import { LoadingMessage, StyledWrapper } from '../Atoms';
import { useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';

type Props = {
  bookId?: string;
  type: CurriculumGoalType;
};
const TrajectoryCurriculumGoals: FC<Props> = ({ bookId, type }) => {
  const [goals, setGoals] = useState<IBookCurriculumGoal[]>([]);
  const { t } = useTranslation();
  const currentTrajectoryId = useAppSelector((state) => state.trajectories.currentTrajectoryId);
  const { isAuthor } = useUser();

  const [getCurriculumGoalsByTrajectoryResponse, getCurriculumGoalsByTrajectory] = useFetch<
    IBookCurriculumGoal[]
  >(apiRoutes.getCurriculumGoalsByTrajectory(currentTrajectoryId as string, type));
  const [addCurriculumGoalToTrajectoryResponse, addCurriculumGoalToTrajectory] = useFetch('', {
    method: 'PATCH',
  });
  const [deleteCurriculumGoalFromTrajectoryResponse, deleteCurriculumGoalFromTrajectory] = useFetch(
    '',
    {
      method: 'DELETE',
    }
  );

  useEffect(getCurriculumGoalsByTrajectory, [getCurriculumGoalsByTrajectory]);

  useEffect(() => {
    if (getCurriculumGoalsByTrajectoryResponse.success) {
      setGoals(getCurriculumGoalsByTrajectoryResponse.data);
    }
  }, [getCurriculumGoalsByTrajectoryResponse]);

  useEffect(() => {
    if (addCurriculumGoalToTrajectoryResponse.success) {
      getCurriculumGoalsByTrajectory();
    }
  }, [addCurriculumGoalToTrajectoryResponse, getCurriculumGoalsByTrajectory]);

  useEffect(() => {
    if (deleteCurriculumGoalFromTrajectoryResponse.success) {
      getCurriculumGoalsByTrajectory();
    }
  }, [deleteCurriculumGoalFromTrajectoryResponse, getCurriculumGoalsByTrajectory]);

  const handleAddGoal = useCallback(
    (goal: IBookCurriculumGoal) => {
      addCurriculumGoalToTrajectory({
        path: apiRoutes.addCurriculumGoalToTrajectory(currentTrajectoryId!, goal.id),
        body: {
          type: type,
        },
      });
    },
    [addCurriculumGoalToTrajectory, currentTrajectoryId, type]
  );

  const handleDeleteGoal = useCallback(
    (goal: IBookCurriculumGoal) => {
      deleteCurriculumGoalFromTrajectory({
        path: apiRoutes.deleteCurriculumGoalFromTrajectory(
          currentTrajectoryId as string,
          goal.id,
          type
        ),
      });
    },
    [deleteCurriculumGoalFromTrajectory, currentTrajectoryId, type]
  );

  return (
    <StyledWrapper>
      {isAuthor && (
        <Typography variant="h3">{t(`trajectory.sidebar.curriculumGoals.${type}`)}</Typography>
      )}
      {getCurriculumGoalsByTrajectoryResponse.pending && (
        <LoadingMessage>{t('shared.loading')}</LoadingMessage>
      )}
      {getCurriculumGoalsByTrajectoryResponse.error &&
        getCurriculumGoalsByTrajectoryResponse.code !== 403 && (
          <Message type="error">{getCurriculumGoalsByTrajectoryResponse.reason}</Message>
        )}
      {getCurriculumGoalsByTrajectoryResponse.success && (
        <>
          <TrajectoryCurriculumGoalsList addedGoals={goals} onDeleteGoal={handleDeleteGoal} />
          {isAuthor && (
            <CurriculumGoalsByTypeCombobox
              addedGoals={goals}
              bookId={bookId}
              type={type}
              onAddGoal={handleAddGoal}
            />
          )}
        </>
      )}
    </StyledWrapper>
  );
};

export default TrajectoryCurriculumGoals;
